import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { updateConsentFormData } from '@/store/slices/formSlice'
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { FormSection as FormSectionInterface } from 'src/types/FormSectionInterface'
import { OrderFormInfoData } from 'src/types/RequisitionFormTypes'

interface ConsentFormData {
  medicalProfessionalName: string
  npi: string
  signatureDate: string
  signature: string
}

interface ConsentProps {
  initialData?: Record<string, unknown>
  onDataChange?: (data: ConsentFormData) => void
  data?: OrderFormInfoData
}

const Consent = forwardRef<FormSectionInterface, ConsentProps>(({ initialData, onDataChange, data }, ref) => {
  const dispatch = useAppDispatch()
  const { formData } = useAppSelector((state) => state.form)

  // Use Redux state as the source of truth, fallback to props for initial data
  const consentData = formData.consentFormData || {
    medicalProfessionalName: String(initialData?.medicalProfessionalName || data?.medicalProfessionalInfo?.name || ''),
    npi: String(initialData?.npi || data?.medicalProfessionalInfo?.npi || ''),
    signatureDate: String(initialData?.signatureDate || new Date().toISOString().split('T')[0]),
    signature: String(initialData?.signature || ''),
  }

  // Initialize Redux state with initial data if not already set
  useEffect(() => {
    if (!formData.consentFormData && (initialData || data)) {
      const initialConsentData = {
        medicalProfessionalName: String(
          initialData?.medicalProfessionalName || data?.medicalProfessionalInfo?.name || ''
        ),
        npi: String(initialData?.npi || data?.medicalProfessionalInfo?.npi || ''),
        signatureDate: String(initialData?.signatureDate || new Date().toISOString().split('T')[0]),
        signature: String(initialData?.signature || ''),
      }
      dispatch(updateConsentFormData(initialConsentData))
    }
  }, [formData.consentFormData, initialData, data, dispatch])

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true)
    const canvas = canvasRef.current
    if (canvas) {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        const rect = canvas.getBoundingClientRect()
        ctx.beginPath()
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top)
      }
    }
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    const canvas = canvasRef.current
    if (canvas) {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        const rect = canvas.getBoundingClientRect()
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top)
        ctx.stroke()
      }
    }
  }

  const stopDrawing = () => {
    setIsDrawing(false)
    const canvas = canvasRef.current
    if (canvas) {
      const signatureData = canvas.toDataURL()
      const updateData = { signature: signatureData }

      // Update Redux state
      dispatch(updateConsentFormData(updateData))

      // Also call the callback for backward compatibility
      const newData = { ...consentData, ...updateData }
      onDataChange?.(newData)
    }
  }

  const clearSignature = () => {
    const canvas = canvasRef.current
    if (canvas) {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const updateData = { signature: '' }

        // Update Redux state
        dispatch(updateConsentFormData(updateData))

        // Also call the callback for backward compatibility
        const newData = { ...consentData, ...updateData }
        onDataChange?.(newData)
      }
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (canvas) {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.strokeStyle = '#000'
        ctx.lineWidth = 2
        ctx.lineCap = 'round'
      }
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    const updateData = { [name]: value }

    // Update Redux state
    dispatch(updateConsentFormData(updateData))

    // Also call the callback for backward compatibility
    const newData = { ...consentData, ...updateData }
    onDataChange?.(newData)
  }

  // Extract the consent form JSX to avoid duplication
  const renderConsentForm = () => (
    <div className="space-y-6">
      {/* Description Section */}
      <div>
        <p className="mb-4 mt-2 text-sm text-gray-600">
          <strong>The undersigned person (or representative thereof) certifies as follows:</strong>
        </p>
        <ol className="ml-4 space-y-2 text-sm text-gray-600">
          <li className="list-decimal">I am a licensed medical professional authorized to order genetic testing;</li>
          <li className="list-decimal">
            Appropriate informed consent by the patient and/or parent/guardian has been obtained and documented;
            <ol className="ml-6 mt-1 list-[lower-alpha] space-y-1">
              <li>Testing will not proceed until all relevant consents have been obtained.</li>
            </ol>
          </li>
          <li className="list-decimal">Testing as ordered is medically necessary;</li>
          <li className="list-decimal">
            All information on this Test Requisition Form is true and correct to the best of my knowledge;
          </li>
          <li className="list-decimal">
            I agree to the attached <a href="/terms-and-conditions">Terms and Conditions</a> for invoicing, billing and
            payment.
          </li>
        </ol>
      </div>

      {/* Medical Professional Signature Section */}
      <div className="rounded-lg bg-gray-50 p-4">
        <h4 className="text-md mb-4 font-medium text-gray-700">Medical Professional Signature</h4>
        <div className="space-y-4">
          <div>
            <label htmlFor="medicalProfessionalName" className="mb-1 block text-sm font-medium text-gray-700">
              Medical Professional Name *
            </label>
            <input
              type="text"
              id="medicalProfessionalName"
              name="medicalProfessionalName"
              value={consentData.medicalProfessionalName || ''}
              onChange={handleInputChange}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Medical Professional Name Print"
            />
          </div>

          <div>
            <label htmlFor="npi" className="mb-1 block text-sm font-medium text-gray-700">
              NPI (National Provider Identifier) *
            </label>
            <input
              type="text"
              id="npi"
              name="npi"
              value={consentData.npi || ''}
              onChange={handleInputChange}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Medical Professional NPI #"
              pattern="[0-9]{10}"
              maxLength={10}
            />
          </div>

          <div>
            <label htmlFor="signatureDate" className="mb-1 block text-sm font-medium text-gray-700">
              Today's Date *
            </label>
            <input
              type="date"
              id="signatureDate"
              name="signatureDate"
              value={consentData.signatureDate || new Date().toISOString().split('T')[0]}
              onChange={handleInputChange}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Medical Professional Signature *</label>
            <div className="rounded-md border-2 border-gray-300 bg-white p-2">
              <canvas
                ref={canvasRef}
                width={400}
                height={80}
                className="w-full cursor-crosshair rounded border border-gray-200"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
              />
            </div>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={clearSignature}
                className="rounded bg-gray-200 px-3 py-1 text-sm text-gray-700 transition-colors hover:bg-gray-300"
              >
                Clear
              </button>
              {consentData.signature && (
                <span className="flex items-center text-sm text-green-600">✓ Signature captured</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  // Implement FormSectionInterface
  useImperativeHandle(ref, () => ({
    isSectionComplete: () => {
      return !!(
        consentData.medicalProfessionalName &&
        consentData.npi &&
        consentData.signatureDate &&
        consentData.signature
      )
    },
    renderSection: () => {
      return renderConsentForm()
    },
    renderSectionInfo: () => {
      return null
    },
  }))

  return renderConsentForm()
})

Consent.displayName = 'Consent'

export default Consent
